import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLanguage } from "../../../shared/contexts/LanguageContext";
import ResponsiveFormLayout from "../../../shared/components/layout/ResponsiveFormLayout";
import CooperationLoader from "../../../shared/components/feedback/CooperationLoader";
import { isMockEnabled, getMockRequestDetail } from "../../../shared/dev/mockData";
import { AuthApiService } from "../../auth/api/AuthApiService";

const ClosureRequestDetailScreen: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const { t, isRTL, language } = useLanguage();
  const navigate = useNavigate();
  const { requestId } = useParams<{ requestId: string }>();

  useEffect(() => {
    loadClosureRequestDetail();
  }, [requestId]);

  // Hide/show logout and language buttons when lightbox is open
  useEffect(() => {
    if (selectedImage) {
      document.body.classList.add('lightbox-open');
    } else {
      document.body.classList.remove('lightbox-open');
    }

    return () => {
      document.body.classList.remove('lightbox-open');
    };
  }, [selectedImage]);

  const loadClosureRequestDetail = async () => {
    if (!requestId) {
      navigate("/requests", { replace: true });
      return;
    }

    setLoading(true);
    setError(null);

    // Mock mode - return mock data
    if (isMockEnabled()) {
      setRequest(getMockRequestDetail());
      setLoading(false);
      return;
    }

    try {
      const authApi = AuthApiService.getInstance();
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const response = await authApi.fetchWithAuth(
        `${baseUrl}/clinic/doctor/closure/${requestId}/with-attachments`
      );

      if (!response.ok) {
        if (response.status === 404) {
          setError(t("request_not_found"));
        } else {
          setError(t("failed_to_load_request_details"));
        }
        setLoading(false);
        return;
      }

      const result = await response.json();
      setRequest(result.data || result);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("failed_to_load_request_details"));
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed top-0 left-0 right-0 bottom-0 bg-white flex justify-center items-center">
        <CooperationLoader />
      </div>
    );
  }

  if (error || !request) {
    return (
      <ResponsiveFormLayout
        showLanguageToggle={true}
        icon="⚠️"
        iconSize="w-24 h-24 text-6xl"
        title={t("error")}
        subtitle={error || t("request_not_found")}
        maxHeight="90vh"
      >
        <button
          onClick={() => navigate("/requests")}
          className="w-full min-h-[56px] px-6 py-4 rounded-2xl bg-[#2E5D7D] text-white text-base font-semibold"
        >
          {t("back_to_requests")}
        </button>
      </ResponsiveFormLayout>
    );
  }

  const InfoRow = ({ label, value }: { label: string; value: string | number | null | undefined }) => {
    if (!value) return null;
    return (
      <div dir={isRTL ? 'rtl' : 'ltr'} className="flex justify-between items-start py-2 border-b border-gray-100">
        <span className="text-sm text-gray-600 font-medium">{label}:</span>
        <span className="text-sm text-gray-900 font-semibold">{value}</span>
      </div>
    );
  };

  const getStatusColor = (status: string) => {
    if (status === 'pending') return 'bg-yellow-50 border-yellow-200';
    if (status === 'draft') return 'bg-blue-50 border-blue-200';
    if (status === 'submitted') return 'bg-blue-50 border-blue-200';
    if (status === 'in_review') return 'bg-purple-50 border-purple-200';
    if (status === 'approved') return 'bg-green-50 border-green-200';
    if (status === 'rejected') return 'bg-red-50 border-red-200';
    return 'bg-gray-50 border-gray-200';
  };

  const getStatusIcon = (status: string) => {
    if (status === 'pending') return '⏳';
    if (status === 'draft') return '📝';
    if (status === 'submitted') return '📤';
    if (status === 'in_review') return '🔍';
    if (status === 'approved') return '✅';
    if (status === 'rejected') return '❌';
    return '📄';
  };

  // Helper function to translate data values
  const translateValue = (value: string | number | null | undefined): string => {
    if (!value) return '';

    const stringValue = String(value);

    // Try to translate common data values
    const capitalizedValue = stringValue.charAt(0).toUpperCase() + stringValue.slice(1).toLowerCase();

    if (t(capitalizedValue) !== capitalizedValue) {
      return t(capitalizedValue);
    }

    if (t(stringValue.toLowerCase()) !== stringValue.toLowerCase()) {
      return t(stringValue.toLowerCase());
    }

    return stringValue;
  };

  return (
    <>
      <ResponsiveFormLayout
        showLanguageToggle={true}
        icon="❌"
        iconSize="w-24 h-24 text-6xl"
        title={t("closure_request_details") || "Closure Request Details"}
        subtitle={request.name || t("clinic_closure")}
        maxHeight="90vh"
        showBackButton={false}
      >
        <div className="w-full space-y-6">
          {/* Request Status Badge */}
          <div className={`p-4 rounded-xl border ${getStatusColor(request.status)}`}>
            <div className="flex items-center justify-center gap-2">
              <span className="text-2xl">{getStatusIcon(request.status)}</span>
              <span className="font-semibold text-lg">
                {t(`status_${request.status}`) || request.status}
              </span>
            </div>
            {request.status === 'pending' && (
              <p className="text-sm text-center text-gray-600 mt-2">
                {t("closure_request_pending_message") || "Your closure request is being processed."}
              </p>
            )}
            {request.status === 'in_review' && (
              <p className="text-sm text-center text-gray-600 mt-2">
                {t("closure_request_review_message") || "Your closure request is under review."}
              </p>
            )}
          </div>

          {/* Inspector Information - only show during pending_inspection */}
          {request.status === 'pending_inspection' && request.inspector && (
            <div className="p-5 rounded-xl border border-gray-200 bg-gradient-to-br from-teal-50 to-cyan-50">
              <h3 className={`text-lg font-bold mb-4 text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
                🔍 {t("inspector_info")}
              </h3>
              <div className="space-y-1 bg-white p-4 rounded-lg">
                <InfoRow label={t("inspector_name")} value={request.inspector.full_name} />
                <InfoRow label={t("inspector_phone")} value={request.inspector.phone_number} />
              </div>
            </div>
          )}

          {/* Request Information */}
          <div className="p-5 rounded-xl border border-gray-200 bg-gradient-to-br from-red-50 to-orange-50">
            <h3 className={`text-lg font-bold mb-4 text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
              📄 {t("request_information")}
            </h3>
            <div className="space-y-1 bg-white p-4 rounded-lg">
              <InfoRow label={t("request_type")} value={t("clinic_closure") || "Clinic Closure"} />
              {request.request_number && (
                <InfoRow label={t("request_number")} value={request.request_number} />
              )}
              <InfoRow
                label={t("submitted_on")}
                value={request.created_at ? new Date(request.created_at).toLocaleDateString() : null}
              />
              <InfoRow
                label={t("last_updated")}
                value={request.updated_at ? new Date(request.updated_at).toLocaleDateString() : null}
              />
            </div>
          </div>

          {/* Clinic Information */}
          {request.name && (
            <div className="p-5 rounded-xl border border-gray-200 bg-gradient-to-br from-gray-50 to-slate-50">
              <h3 className={`text-lg font-bold mb-4 text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
                🏥 {t("clinic_information")}
              </h3>
              <div className="space-y-1 bg-white p-4 rounded-lg">
                <InfoRow label={t("clinic_name")} value={request.name} />
                <InfoRow label={t("governorate")} value={translateValue(request.governorate)} />
                <InfoRow label={t("city")} value={isRTL ? request.area?.name_ar : request.area?.name} />
                {request.neighborhood && (
                  <InfoRow label={t("neighborhood")} value={request.neighborhood} />
                )}
                {request.street && (
                  <InfoRow label={t("street")} value={request.street} />
                )}
                {request.phone_primary && (
                  <InfoRow label={t("phone_primary")} value={request.phone_primary} />
                )}
              </div>
            </div>
          )}

          {/* Closure Details */}
          <div className="p-5 rounded-xl border border-gray-200 bg-gradient-to-br from-amber-50 to-yellow-50">
            <h3 className={`text-lg font-bold mb-4 text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
              📋 {t("closure_details") || "Closure Details"}
            </h3>
            <div className="space-y-1 bg-white p-4 rounded-lg">
              {request.closure_date && (
                <InfoRow
                  label={t("closure_date")}
                  value={new Date(request.closure_date).toLocaleDateString()}
                />
              )}
              {request.closure_reason && (
                <div className="py-2">
                  <span className="text-sm text-gray-600 font-medium block mb-1">
                    {t("closure_reason")}:
                  </span>
                  <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">
                    {request.closure_reason}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Documents/Attachments */}
          {request.documents && Object.keys(request.documents).length > 0 && (
            <div className="p-5 rounded-xl border border-gray-200 bg-gradient-to-br from-indigo-50 to-purple-50">
              <h3 className={`text-lg font-bold mb-4 text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
                📎 {t("documents")}
              </h3>
              <div className="space-y-4 bg-white p-4 rounded-lg">
                {Object.entries(request.documents)
                  .filter(([_, value]) => value)
                  .map(([key, value]: [string, any]) => {
                    const formatKey = (k: string) => {
                      // Try to translate first
                      const translated = t(k);
                      if (translated !== k) return translated;

                      return k
                        .split('_')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ');
                    };

                    // Handle array of documents
                    if (Array.isArray(value)) {
                      return (
                        <div key={key} className="border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-xl">📁</span>
                            <span className="text-sm font-semibold text-gray-900">
                              {formatKey(key)} ({value.length} {value.length === 1 ? t("file") : t("files")})
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            {value.map((url: string, index: number) => (
                              <div
                                key={index}
                                onClick={() => setSelectedImage(url)}
                                className="relative cursor-pointer group rounded-lg overflow-hidden border-2 border-gray-200 hover:border-[#c03744] transition-all"
                              >
                                <img
                                  src={url}
                                  alt={`${formatKey(key)} ${index + 1}`}
                                  className="w-full h-40 object-cover"
                                  loading="lazy"
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                                  <span className="text-white text-2xl opacity-0 group-hover:opacity-100 transition-all">🔍</span>
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2">
                                  <span className="text-white text-xs font-medium">
                                    {formatKey(key)} {index + 1}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }

                    // Handle single document
                    return (
                      <div key={key} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-xl">📄</span>
                          <span className="text-sm font-semibold text-gray-900">{formatKey(key)}</span>
                        </div>
                        <div
                          onClick={() => setSelectedImage(value)}
                          className="relative cursor-pointer group rounded-lg overflow-hidden border-2 border-gray-200 hover:border-[#c03744] transition-all"
                        >
                          <img
                            src={value}
                            alt={formatKey(key)}
                            className="w-full h-48 object-cover"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                            <span className="text-white text-3xl opacity-0 group-hover:opacity-100 transition-all">🔍</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Review Notes (if rejected) */}
          {request.status === 'rejected' && request.review_notes && (
            <div className="p-5 rounded-xl border-2 border-red-300 bg-gradient-to-br from-red-50 to-orange-50">
              <h3 className={`text-lg font-bold mb-4 text-red-800 ${isRTL ? "text-right" : "text-left"}`}>
                ❌ {t("rejection_reason")}
              </h3>
              <div className="bg-white p-4 rounded-lg">
                <p className="text-sm text-gray-900">{request.review_notes}</p>
              </div>
            </div>
          )}
        </div>
      </ResponsiveFormLayout>

      {/* Image Modal/Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center p-4"
          style={{ zIndex: 9999999 }}
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative w-full h-full flex items-center justify-center">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 left-4 text-white text-5xl hover:text-gray-300 transition-all z-10 flex items-center gap-2"
            >
              <span className="text-4xl">←</span>
              <span className="text-lg font-semibold">{t("close")}</span>
            </button>

            <img
              src={selectedImage}
              alt={t("document_preview")}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ClosureRequestDetailScreen;
